import { useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { getAbsenceReferenceSelection } from '../../lib';
import useTimeline from './timeline';

type AbsenceRouteSearch = {
  absenceId?: number | string;
  date?: string;
};

/**
 * Opens the details panel for an absence named in the URL.
 *
 * Task references land here with `?absenceId=&date=`. The absence itself is
 * usually outside the month currently on screen, so this moves the calendar to
 * the date first and waits for that range to load before selecting -- the
 * absence simply is not in memory before then.
 */
export default function useAbsenceRouteFocus(): void {
  const { absences, calendar, focusAbsence, visibleRange } = useTimeline();
  const search = useSearch({ strict: false }) as AbsenceRouteSearch;
  const navigate = useNavigate();

  const absenceId = search.absenceId ? Number(search.absenceId) : null;
  const targetDate = search.date ?? null;

  const focusedRef = useRef<number | null>(null);

  // Driven by the visible range rather than a one-shot flag, so a move that
  // lands before the calendar is mounted simply happens again on the next
  // render instead of leaving the target permanently out of reach.
  useEffect(() => {
    if (!absenceId || !targetDate) {
      return;
    }
    const anchor = new Date(`${targetDate}T00:00:00`);
    if (anchor >= visibleRange.start && anchor < visibleRange.end) {
      return;
    }
    calendar.setMonthViewFrom(
      new Date(anchor.getFullYear(), anchor.getMonth(), 1),
    );
  }, [absenceId, targetDate, visibleRange, calendar]);

  useEffect(() => {
    if (!absenceId || focusedRef.current === absenceId) {
      return;
    }

    const selection = getAbsenceReferenceSelection(absenceId, absences);
    if (!selection?.start) {
      // Range still loading, or the absence is gone; try again on next fetch.
      return;
    }

    focusedRef.current = absenceId;
    focusAbsence(selection, new Date(`${selection.start}T00:00:00`), (rows) =>
      getAbsenceReferenceSelection(absenceId, rows),
    );
    navigate({ to: '/absence', search: {}, replace: true });
  }, [absenceId, absences, focusAbsence, navigate]);
}
